import React, { useState, useMemo, useRef, useEffect } from 'react'
import styled from 'styled-components'
import Input from 'components/atoms/Input'
import Checkbox from 'components/atoms/Checkbox'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Chip from '@mui/material/Chip'
import useDebounce from 'utils/hooks/useDebounce'
import { getFileUploaders } from 'services/fileService'

const UploaderMultiSelect = ({
  selectedUploaders = [],
  setSelectedUploaders,
  placeholder = 'Search and select uploaders...',
  bpId = null,
  files = [] // Keep for backwards compatibility
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [showSelected, setShowSelected] = useState(false)
  const [uploaderOptions, setUploaderOptions] = useState([])
  const [loadingUploaders, setLoadingUploaders] = useState(false)
  const resultsRef = useRef(null)
  const inputRef = useRef(null)
  const debouncedValue = useDebounce(searchQuery, 300)

  // Fetch uploaders from API when bpId is available
  useEffect(() => {
    const fetchUploaders = async () => {
      if (!bpId) return

      setLoadingUploaders(true)
      try {
        const response = await getFileUploaders(bpId)
        const data = response.data?.data || response.data || []
        setUploaderOptions(data)
      } catch (error) {
        console.error('Failed to fetch uploaders:', error)
        setUploaderOptions([])
      } finally {
        setLoadingUploaders(false)
      }
    }

    fetchUploaders()
  }, [bpId])

  // Fallback: Extract uploaders from files if no bpId provided (backwards compat)
  useEffect(() => {
    if (bpId || !files || files.length === 0) return

    const uniqueUploaders = new Map()
    files.forEach(file => {
      if (file.uploader) {
        const uploaderId = file.uploader.id
        if (!uniqueUploaders.has(uploaderId)) {
          uniqueUploaders.set(uploaderId, {
            id: uploaderId,
            name: `${file.uploader.firstName} ${file.uploader.lastName}`.trim() || file.uploader.email,
            email: file.uploader.email,
            firstName: file.uploader.firstName,
            lastName: file.uploader.lastName
          })
        }
      }
    })

    setUploaderOptions(
      Array.from(uniqueUploaders.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    )
  }, [bpId, files])

  // Filter uploaders based on search query
  const filteredUploaders = useMemo(() => {
    if (!debouncedValue) return uploaderOptions
    
    const query = debouncedValue.toLowerCase().trim()
    return uploaderOptions.filter(uploader => {
      const searchableText = [
        uploader.name,
        uploader.email,
        uploader.firstName,
        uploader.lastName
      ].filter(Boolean).join(' ').toLowerCase()
      
      return searchableText.includes(query)
    })
  }, [uploaderOptions, debouncedValue])

  // Get selected uploader names for display
  const selectedUploaderNames = useMemo(() => {
    if (!selectedUploaders.length || !uploaderOptions.length) return []
    return selectedUploaders
      .map(id => uploaderOptions.find(uploader => uploader.id === id)?.name)
      .filter(Boolean)
  }, [selectedUploaders, uploaderOptions])

  const handleUploaderToggle = (uploaderId) => {
    const newSelected = selectedUploaders.includes(uploaderId)
      ? selectedUploaders.filter(id => id !== uploaderId)
      : [...selectedUploaders, uploaderId]
    setSelectedUploaders(newSelected)
  }

  const handleClearAll = () => {
    setSelectedUploaders([])
  }

  const handleRemoveUploader = (uploaderId) => {
    setSelectedUploaders(selectedUploaders.filter(id => id !== uploaderId))
  }

  const handleSearch = (_, query) => {
    setSearchQuery(query)
  }

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        const input = inputRef.current.querySelector('input')
        if (input) {
          input.focus()
        }
      }, 100)
    }
  }, [open])

  const displayText = selectedUploaders.length > 0 
    ? `${selectedUploaders.length} uploader${selectedUploaders.length > 1 ? 's' : ''} selected`
    : placeholder

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Container>
        <InputWrapper onClick={() => setOpen(true)}>
          <DisplayInput hasValue={selectedUploaders.length > 0}>
            <DisplayText hasValue={selectedUploaders.length > 0}>
              {displayText}
            </DisplayText>
            {selectedUploaders.length > 0 && (
              <ActionButtons>
                <ActionButton onClick={(e) => { e.stopPropagation(); setShowSelected(!showSelected); }}>
                  {showSelected ? 'Hide' : 'Show'} Selected
                </ActionButton>
                <ActionButton onClick={(e) => { e.stopPropagation(); handleClearAll(); }}>
                  Clear All
                </ActionButton>
              </ActionButtons>
            )}
          </DisplayInput>
          <DropdownIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </DropdownIcon>
        </InputWrapper>

        {/* Selected uploaders display */}
        {selectedUploaders.length > 0 && showSelected && (
          <SelectedWrapper>
            {selectedUploaderNames.map((uploaderName, index) => (
              <StyledChip
                key={selectedUploaders[index]}
                label={uploaderName}
                onDelete={() => handleRemoveUploader(selectedUploaders[index])}
                size="small"
              />
            ))}
          </SelectedWrapper>
        )}

        {open && (
          <Dropdown ref={resultsRef}>
            <SearchWrapper>
              <Input
                ref={inputRef}
                onChange={handleSearch}
                value={searchQuery}
                name='searchQuery'
                placeholder='Search uploaders...'
                nomarginbottom
              />
            </SearchWrapper>
            
            <ResultsWrapper>
              {filteredUploaders.length === 0 ? (
                <NoResults>No uploaders found</NoResults>
              ) : (
                filteredUploaders.map(uploader => (
                  <UploaderItem key={uploader.id}>
                    <Checkbox
                      label={uploader.name}
                      name={uploader.id}
                      value={selectedUploaders.includes(uploader.id)}
                      onChange={() => handleUploaderToggle(uploader.id)}
                      noMargin
                    />
                    {uploader.email && uploader.name !== uploader.email && (
                      <UploaderEmail>{uploader.email}</UploaderEmail>
                    )}
                  </UploaderItem>
                ))
              )}
            </ResultsWrapper>
            
            {selectedUploaders.length > 0 && (
              <DropdownFooter>
                <FooterText>{selectedUploaders.length} selected</FooterText>
                <FooterButton onClick={handleClearAll}>Clear All</FooterButton>
              </DropdownFooter>
            )}
          </Dropdown>
        )}
      </Container>
    </ClickAwayListener>
  )
}

export default UploaderMultiSelect

const Container = styled.div`
  position: relative;
  width: 100%;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const InputWrapper = styled.div`
  position: relative;
  cursor: pointer;
`

const DisplayInput = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey[300]};
  border-radius: 6px;
  padding: 10px 35px 10px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  min-height: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`

const DisplayText = styled.span`
  color: ${({ theme, hasValue }) => hasValue ? theme.colors.grey[700] : theme.colors.grey[500]};
  font-weight: 400;
`

const DropdownIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.54);
  pointer-events: none;
  display: flex;
  align-items: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`

const SelectedWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 100px;
  overflow-y: auto;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.primary}05;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.primary}10;
`

const StyledChip = styled(Chip)`
  && {
    height: 24px;
    font-size: 11px;
    
    .MuiChip-deleteIcon {
      font-size: 14px;
    }
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  margin-top: 4px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const SearchWrapper = styled.div`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
`

const ResultsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grey[100]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grey[400]};
    border-radius: 3px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.grey[500]};
    }
  }
`

const UploaderItem = styled.div`
  padding: 10px 16px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[50]};
    padding-left: 20px;
  }
`

const UploaderEmail = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-top: 4px;
  margin-left: 24px;
  line-height: 1.3;
`

const NoResults = styled.div`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 14px;
`

const DropdownFooter = styled.div`
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.grey[50]}50;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FooterText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
`

const FooterButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`